/**
 * Third-party Services Hub
 *
 * Ported from src/views/admin/Services/index.jsx (react-router v5, JS).
 *
 * Monitors every external integration NUREV depends on:
 *   - Brevo       (transactional email)
 *   - Twilio      (transactional SMS)
 *   - Sentry      (error tracking)
 *   - Better Stack (uptime monitoring)
 *   - PostHog     (product analytics)
 *   - Cloudflare  (Turnstile bot protection)
 *
 * Differences from legacy:
 *   - Tab state is carried in the URL search param `?tab=` via
 *     react-router v7's useSearchParams (replaces useLocation/useHistory v5).
 *   - Data fetching uses react-query (useVendorStatus) with 60s polling
 *     rather than a raw useEffect/setInterval in the component.
 *   - StatusPill / AnimatedCounter / Sparkline / EventFeed are
 *     dashboard-only components not available here; equivalents are
 *     inlined using the admin console's atoms.tsx + tailwind tokens.
 *   - Amber accent throughout (admin console convention).
 */
import { useSearchParams } from 'react-router-dom';
import {
  Mail,
  MessageSquare,
  Eye,
  Activity,
  BarChart2,
  Shield,
  Phone,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import {
  BrevoData,
  BetterStackData,
  BetterStackMonitor,
  CloudflareData,
  PostHogData,
  QuoData,
  QuoNumber,
  SentryData,
  TwilioData,
  VendorData,
  VendorId
} from '@/api/adminServices';
import { useVendorStatus } from '@/hooks/useAdminServices';
import { Card, SectionLabel, Skeleton } from '@/components/ui/atoms';
import { fmtNum, fmtDateTime } from '@/lib/format';

// ─── Vendor registry ─────────────────────────────────────────────────────
// Mirror source VENDORS array verbatim.

interface Vendor {
  id: VendorId;
  label: string;
  icon: React.ElementType;
  purpose: string;
  url: string;
}

const VENDORS: Vendor[] = [
  { id: 'brevo',        label: 'Brevo',        icon: Mail,          purpose: 'Transactional email',      url: 'https://app.brevo.com'          },
  { id: 'twilio',       label: 'Twilio',        icon: MessageSquare, purpose: 'Transactional SMS',        url: 'https://console.twilio.com'     },
  { id: 'sentry',       label: 'Sentry',        icon: Eye,           purpose: 'Error tracking',           url: 'https://sentry.io'              },
  { id: 'better-stack', label: 'Better Stack',  icon: Activity,      purpose: 'Uptime monitoring',        url: 'https://uptime.betterstack.com' },
  { id: 'posthog',      label: 'PostHog',       icon: BarChart2,     purpose: 'Product analytics',        url: 'https://us.posthog.com'         },
  { id: 'cloudflare',   label: 'Cloudflare',    icon: Shield,        purpose: 'Turnstile bot protection', url: 'https://dash.cloudflare.com'    },
  { id: 'quo',          label: 'Quo',           icon: Phone,         purpose: 'Business phone line',      url: 'https://app.quo.com'            },
];

// ─── Helpers ──────────────────────────────────────────────────────────────

/** Mini KPI card — replaces the legacy KpiCard helper. */
function KpiCard({
  label,
  value,
  bad,
  good
}: {
  label: string;
  value: string | number;
  bad?: boolean;
  good?: boolean;
}) {
  const valueColor = bad
    ? 'text-red-300'
    : good
      ? 'text-emerald-300'
      : 'text-nurev-text';
  return (
    <Card className="p-4">
      <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
        {label}
      </p>
      <p className={`text-xl font-bold ${valueColor}`}>
        {typeof value === 'number' ? fmtNum(value) : (value ?? '—')}
      </p>
    </Card>
  );
}

/** Replaces the legacy FeedCard + EventFeed. */
function FeedCard({
  title,
  events,
  emptyText = 'No recent activity'
}: {
  title: string;
  events: Array<Record<string, unknown>>;
  emptyText?: string;
}) {
  return (
    <Card>
      <SectionLabel>{title}</SectionLabel>
      {events.length === 0 ? (
        <p className="text-sm text-nurev-muted">{emptyText}</p>
      ) : (
        <div className="max-h-72 divide-y divide-nurev-border overflow-y-auto">
          {events.map((ev, i) => {
            const msg =
              typeof ev.message === 'string'
                ? ev.message
                : typeof ev.name === 'string'
                  ? ev.name
                  : JSON.stringify(ev);
            const ts =
              typeof ev.timestamp === 'string' || ev.timestamp instanceof Date
                ? fmtDateTime(ev.timestamp as string)
                : null;
            const level = typeof ev.level === 'string' ? ev.level : null;
            const levelColor =
              level === 'error' || level === 'fatal'
                ? 'text-red-300'
                : level === 'warning'
                  ? 'text-amber-300'
                  : 'text-nurev-muted';
            return (
              <div key={i} className="flex items-start justify-between gap-3 py-2">
                <p className="min-w-0 flex-1 text-xs text-nurev-text">{msg}</p>
                <div className="flex shrink-0 flex-col items-end gap-0.5">
                  {level && (
                    <span className={`text-[10px] font-medium ${levelColor}`}>
                      {level}
                    </span>
                  )}
                  {ts && (
                    <span className="text-[10px] text-nurev-muted">{ts}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Not-connected empty state ────────────────────────────────────────────

function NotConnected({ vendor }: { vendor: Vendor }) {
  const Icon = vendor.icon;
  return (
    <div className="rounded-2xl border border-nurev-border bg-nurev-surface p-8 text-center">
      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-nurev-border bg-white/[0.04]">
        <Icon className="h-6 w-6 text-nurev-muted" />
      </div>
      <p className="mb-1 text-sm font-bold text-nurev-text">
        {vendor.label} not connected
      </p>
      <p className="mx-auto mb-4 max-w-sm text-xs leading-relaxed text-nurev-muted">
        Add credentials in your K8s secret to enable live monitoring. The
        backend endpoint{' '}
        <code className="mx-1 rounded bg-white/[0.04] px-1 py-0.5 font-mono text-[10px] text-amber-300">
          /admin-services/{vendor.id}
        </code>
        will start returning data automatically once the integration is
        configured.
      </p>
      <a
        href={vendor.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-lg border border-nurev-border bg-white/[0.04] px-3.5 py-1.5 text-xs font-bold text-nurev-text no-underline transition-colors hover:bg-white/[0.07]"
      >
        Open {vendor.label} dashboard <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}

// ─── Per-vendor panels ────────────────────────────────────────────────────

function BrevoPanel({ data }: { data: BrevoData }) {
  const sentToday = data.sent24h ?? 0;
  const bounced   = data.bounce24h ?? 0;
  const spamRate  = data.spamRate ?? 0;
  const queueLen  = data.queueLength ?? 0;
  const events    = Array.isArray(data.events) ? data.events : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Sent (24h)"   value={sentToday} />
        <KpiCard label="Bounced"      value={bounced}   bad={bounced > sentToday * 0.05} />
        <KpiCard label="Spam rate"    value={`${(spamRate * 100).toFixed(2)}%`} />
        <KpiCard label="Queue depth"  value={queueLen}  bad={queueLen > 100} />
      </div>
      <FeedCard title="Recent send activity" events={events} />
    </div>
  );
}

function TwilioPanel({ data }: { data: TwilioData }) {
  const sent24h   = data.sent24h ?? 0;
  const failed24h = data.failed24h ?? 0;
  const cost24h   = data.cost24h ?? 0;
  const events    = Array.isArray(data.events) ? data.events : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Sent (24h)"   value={sent24h} />
        <KpiCard label="Failed"        value={failed24h} bad={failed24h > 0} />
        <KpiCard label="Spend (24h)"  value={`$${Number(cost24h).toFixed(2)}`} />
        <KpiCard label="A2P 10DLC"    value={data.a2pStatus ?? '—'} />
      </div>
      <FeedCard title="Recent SMS activity" events={events} />
    </div>
  );
}

function SentryPanel({ data }: { data: SentryData }) {
  const errors24h     = data.errors24h ?? 0;
  const newIssues     = data.newIssues24h ?? 0;
  const releaseHealth = data.releaseHealth ?? 0;
  const events        = Array.isArray(data.events) ? data.events : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Errors (24h)"    value={errors24h}                         bad={errors24h > 100} />
        <KpiCard label="New issues"       value={newIssues}                         bad={newIssues > 5} />
        <KpiCard label="Release health"  value={`${releaseHealth.toFixed(1)}%`}    good={releaseHealth >= 99} />
        <KpiCard label="Crash-free"      value={data.crashFreeRate != null ? `${data.crashFreeRate.toFixed(1)}%` : '—'} />
      </div>
      <FeedCard
        title="Latest issues"
        events={events}
        emptyText="No errors in the last 24 hours"
      />
    </div>
  );
}

function BetterStackPanel({ data }: { data: BetterStackData }) {
  const monitors    = Array.isArray(data.monitors) ? data.monitors : [];
  const incidents24h = data.incidents24h ?? 0;
  const slaPct      = data.sla30d ?? 100;
  const upMonitors  = monitors.filter((m: BetterStackMonitor) => m.status === 'up').length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          label="Monitors up"
          value={`${upMonitors} / ${monitors.length}`}
          good={upMonitors === monitors.length && monitors.length > 0}
        />
        <KpiCard label="Incidents (24h)" value={incidents24h} bad={incidents24h > 0} />
        <KpiCard label="SLA (30d)"       value={`${slaPct.toFixed(2)}%`} good={slaPct >= 99.9} />
        <KpiCard label="MTTR"            value={data.mttr ?? '—'} />
      </div>

      {monitors.length > 0 ? (
        <Card>
          <SectionLabel>Monitor status</SectionLabel>
          <div className="space-y-1.5">
            {monitors.map((m: BetterStackMonitor, i: number) => {
              const isUp       = m.status === 'up';
              const isDegraded = m.status === 'degraded';
              const dotColor   = isUp
                ? 'bg-emerald-400'
                : isDegraded
                  ? 'bg-amber-400'
                  : 'bg-red-400';
              const textColor  = isUp
                ? 'text-emerald-300'
                : isDegraded
                  ? 'text-amber-300'
                  : 'text-red-300';
              return (
                <div
                  key={m.id ?? m.url ?? i}
                  className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.02] px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold text-nurev-text">
                      {m.name ?? m.url ?? '—'}
                    </p>
                    {m.url && (
                      <p className="truncate text-[10px] text-nurev-muted">{m.url}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`h-2 w-2 rounded-full ${dotColor}`} />
                    <span className={`text-[10px] font-medium ${textColor}`}>
                      {m.status ?? 'unknown'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ) : (
        <p className="py-4 text-center text-xs text-nurev-muted">
          No monitors configured yet.
        </p>
      )}
    </div>
  );
}

function PostHogPanel({ data }: { data: PostHogData }) {
  const dau        = data.dau ?? 0;
  const wau        = data.wau ?? 0;
  const sessions24h = data.sessions24h ?? 0;
  const topEvents  = Array.isArray(data.topEvents) ? data.topEvents : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="DAU"            value={dau} />
        <KpiCard label="WAU"            value={wau} />
        <KpiCard label="Sessions (24h)" value={sessions24h} />
        <KpiCard
          label="Conversion"
          value={data.conversion != null ? `${(data.conversion * 100).toFixed(1)}%` : '—'}
        />
      </div>
      <FeedCard
        title="Top events (24h)"
        events={topEvents}
        emptyText="No event data yet"
      />
    </div>
  );
}

function CloudflarePanel({ data }: { data: CloudflareData }) {
  const challenges24h = data.challenges24h ?? 0;
  const blocked24h    = data.blocked24h ?? 0;
  const passed24h     = data.passed24h ?? 0;
  const passRate      = challenges24h > 0 ? (passed24h / challenges24h) * 100 : 0;
  const events        = Array.isArray(data.events) ? data.events : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Challenges (24h)" value={challenges24h} />
        <KpiCard label="Bots blocked"     value={blocked24h}    good={blocked24h > 0} />
        <KpiCard label="Pass rate"        value={`${passRate.toFixed(1)}%`} good={passRate >= 95} />
        <KpiCard label="Top region"       value={data.topRegion ?? '—'} />
      </div>
      <FeedCard
        title="Recent challenges"
        events={events}
        emptyText="No challenge data yet"
      />
    </div>
  );
}

function QuoPanel({ data }: { data: QuoData }) {
  const numberCount = data.numberCount ?? 0;
  const memberCount = data.memberCount ?? 0;
  const numbers     = Array.isArray(data.numbers) ? data.numbers : [];
  const events      = Array.isArray(data.events) ? data.events : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Phone numbers"     value={numberCount} />
        <KpiCard label="Workspace members" value={memberCount} />
        <KpiCard
          label="Messaging"
          value={data.messagingOk ? 'OK' : 'Restricted'}
          good={data.messagingOk === true}
          bad={data.messagingOk === false}
        />
        <KpiCard
          label="Calling"
          value={data.callingOk ? 'OK' : 'Restricted'}
          good={data.callingOk === true}
          bad={data.callingOk === false}
        />
      </div>

      {numbers.length > 0 ? (
        <Card>
          <SectionLabel>Phone numbers</SectionLabel>
          <div className="space-y-1.5">
            {numbers.map((n: QuoNumber, i: number) => {
              const active = (n.status ?? '') === 'active';
              return (
                <div
                  key={n.number ?? i}
                  className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.02] px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold text-nurev-text">
                      {n.number ?? '—'}
                    </p>
                    <p className="truncate text-[10px] text-nurev-muted">
                      {n.name ?? '(unnamed)'}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 text-[10px] font-medium ${
                      active ? 'text-emerald-300' : 'text-amber-300'
                    }`}
                  >
                    {n.status ?? '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      ) : (
        <p className="py-4 text-center text-xs text-nurev-muted">
          No phone numbers in this workspace.
        </p>
      )}

      <FeedCard
        title="Recent number activity"
        events={events}
        emptyText="No recent number changes"
      />
    </div>
  );
}

// ─── Panel dispatcher ─────────────────────────────────────────────────────

function VendorPanel({ id, data }: { id: VendorId; data: VendorData }) {
  switch (id) {
    case 'brevo':        return <BrevoPanel        data={data as BrevoData} />;
    case 'twilio':       return <TwilioPanel       data={data as TwilioData} />;
    case 'sentry':       return <SentryPanel       data={data as SentryData} />;
    case 'better-stack': return <BetterStackPanel  data={data as BetterStackData} />;
    case 'posthog':      return <PostHogPanel      data={data as PostHogData} />;
    case 'cloudflare':   return <CloudflarePanel   data={data as CloudflareData} />;
    case 'quo':          return <QuoPanel          data={data as QuoData} />;
  }
}

// ─── Main screen ──────────────────────────────────────────────────────────

export function ServicesScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as VendorId | null;
  const tab: VendorId =
    tabParam && VENDORS.some(v => v.id === tabParam) ? tabParam : 'brevo';

  const setTab = (id: VendorId) => {
    setSearchParams({ tab: id }, { replace: true });
  };

  const vendor = VENDORS.find(v => v.id === tab) ?? VENDORS[0];
  const { data, isLoading, isError, error, dataUpdatedAt, refetch, isFetching } =
    useVendorStatus(tab);

  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt) : null;

  const isConnected = data !== null && data !== undefined;
  const hasError    = isError && !isLoading;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Third-party Services
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            Monitor every external integration without leaving NUREV.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-[10px] text-nurev-muted">
              updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => void refetch()}
            disabled={isFetching}
            title="Refresh now"
            className="inline-flex items-center gap-1.5 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-xs font-bold text-nurev-text transition-colors hover:bg-white/[0.07] disabled:opacity-50"
          >
            <RefreshCw
              className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Vendor tab strip */}
      <div className="flex flex-wrap gap-2">
        {VENDORS.map(v => {
          const active = v.id === tab;
          const Icon = v.icon;
          return (
            <button
              key={v.id}
              onClick={() => setTab(v.id)}
              className={`inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-xs font-bold transition-colors ${
                active
                  ? 'border-amber-500/60 bg-amber-500/15 text-nurev-text shadow-[0_0_0_3px_rgba(245,158,11,0.12)]'
                  : 'border-nurev-border bg-nurev-surface text-nurev-muted hover:border-amber-500/30 hover:text-nurev-text'
              }`}
            >
              <Icon className={`h-3.5 w-3.5 ${active ? 'text-amber-300' : 'text-nurev-muted'}`} />
              {v.label}
            </button>
          );
        })}
      </div>

      {/* Vendor header card */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-nurev-border bg-nurev-surface-lift p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-nurev-border bg-white/[0.05]">
            {(() => {
              const Icon = vendor.icon;
              return <Icon className="h-5 w-5 text-nurev-muted" />;
            })()}
          </div>
          <div>
            <p className="text-sm font-bold text-nurev-text">{vendor.label}</p>
            <p className="text-[11px] text-nurev-muted">{vendor.purpose}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isLoading ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-medium text-nurev-muted ring-1 ring-white/10">
              <span className="h-1.5 w-1.5 rounded-full bg-nurev-muted" />
              Loading
            </span>
          ) : isConnected ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 px-2.5 py-1 text-[10px] font-medium text-emerald-300 ring-1 ring-emerald-500/30">
              <CheckCircle className="h-3 w-3" />
              Connected
            </span>
          ) : hasError ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 px-2.5 py-1 text-[10px] font-medium text-red-300 ring-1 ring-red-500/30">
              <XCircle className="h-3 w-3" />
              Error
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-medium text-nurev-muted ring-1 ring-white/10">
              <span className="h-1.5 w-1.5 rounded-full bg-nurev-muted" />
              Not connected
            </span>
          )}
          <a
            href={vendor.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-[11px] font-bold text-nurev-text no-underline transition-colors hover:bg-white/[0.07]"
          >
            Vendor dashboard <ExternalLink className="h-2.5 w-2.5" />
          </a>
        </div>
      </div>

      {/* Live error banner (non-404 errors only) */}
      {hasError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
          <AlertCircle className="h-3.5 w-3.5 text-red-400" />
          <span className="text-xs text-red-300">
            {error instanceof Error ? error.message : 'Failed to load'}
          </span>
        </div>
      )}

      {/* Active vendor panel */}
      {isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="p-4">
                <Skeleton className="mb-2 h-3 w-20" />
                <Skeleton className="h-7 w-16" />
              </Card>
            ))}
          </div>
          <Card>
            <Skeleton className="h-32 w-full" />
          </Card>
        </div>
      ) : isConnected ? (
        <VendorPanel id={tab} data={data} />
      ) : (
        <NotConnected vendor={vendor} />
      )}
    </div>
  );
}
